import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import BulkImportStaff from "../components/BulkImportStaff";
import { Upload, Download, Users, Mail, History, Stethoscope, ClipboardList, Search, Briefcase } from "lucide-react";

const SUITE_ICONS = {
  coach: ClipboardList,
  medical: Stethoscope,
  scout: Search,
  support: Briefcase,
};

const SUITE_LABEL = {
  coach: "Coach suite",
  medical: "Medical suite",
  scout: "Scouting suite",
  support: "Support suite",
};

export default function ClubStaff() {
  const [staff, setStaff] = useState([]);
  const [history, setHistory] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    const [a, b] = await Promise.all([
      api.get("/club-staff"),
      api.get("/import/history"),
    ]);
    setStaff(a.data || []);
    setHistory((b.data || []).filter((h) => h.kind === "staff"));
  };
  useEffect(() => { refresh(); }, []);

  const filtered = filter === "all" ? staff : staff.filter((s) => s.suite === filter);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/staff/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "playmaker_staff_upload_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <Layout
      title="Club staff"
      subtitle="Coaching · Medical · Scouts · Support — managed in one place"
      right={
        <div className="flex items-center gap-2">
          <button
            onClick={downloadTemplate}
            className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
            data-testid="staff-page-download-template"
          ><Download size={14} /> Download template</button>
          <button
            onClick={() => setImportOpen(true)}
            className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
            data-testid="staff-page-import-btn"
          ><Upload size={14} /> Import staff</button>
        </div>
      }
    >
      <div className="space-y-6" data-testid="club-staff-root">
        {/* Suite chips */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="staff-suite-filter">
          <Chip label={`All (${staff.length})`} active={filter === "all"} onClick={() => setFilter("all")} testid="staff-filter-all" />
          {Object.entries(SUITE_LABEL).map(([k, lbl]) => {
            const n = staff.filter((s) => s.suite === k).length;
            return <Chip key={k} label={`${lbl} (${n})`} active={filter === k} onClick={() => setFilter(k)} testid={`staff-filter-${k}`} />;
          })}
        </div>

        {/* Staff list */}
        <div className="pm-card divide-y divide-slate-100" data-testid="staff-list">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="staff-empty">
              <Users size={28} className="text-slate-300 mx-auto mb-2" />
              No staff in this view yet. Click <b>Import staff</b> to bulk-add coaches, medical, scouts, and support.
            </div>
          )}
          {filtered.map((s) => {
            const Icon = SUITE_ICONS[s.suite] || Users;
            return (
              <div key={s.id} className="p-4 flex items-center gap-3" data-testid={`staff-card-${s.id}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0">
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                    <span className="text-base">{s.nationality_flag || "🏳️"}</span>
                    {s.name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{s.role} · {SUITE_LABEL[s.suite] || s.suite}</div>
                </div>
                <span className="text-xs text-slate-500 truncate hidden md:inline">{s.email}</span>
                <span className="pm-pill pm-pill-slate text-[10px]">{s.access_level}</span>
                {s.invited && <span className="pm-pill pm-pill-amber text-[10px] inline-flex items-center gap-1"><Mail size={9} /> Invited</span>}
              </div>
            );
          })}
        </div>

        {/* Import history */}
        <section data-testid="staff-import-history-section">
          <div className="flex items-center gap-2 mb-3">
            <History size={14} className="text-slate-400" />
            <h2 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Import history</h2>
          </div>
          <div className="pm-card divide-y divide-slate-100">
            {history.length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="staff-import-history-empty">No staff imports yet.</div>}
            {history.map((h) => (
              <div key={h.id} className="p-4 flex items-center gap-4 text-sm" data-testid={`import-history-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{h.filename}</div>
                  <div className="text-[11px] text-slate-500">{new Date(h.created_at).toLocaleString()} · by {h.imported_by} ({h.imported_by_role})</div>
                </div>
                <div className="text-xs text-slate-700 font-mono-tab">
                  <b>{h.created}</b> created · {h.skipped} skipped · {h.errors_count} errors
                </div>
                {h.invitations_queued > 0 && (
                  <span className="pm-pill pm-pill-amber text-[10px] inline-flex items-center gap-1"><Mail size={9} /> {h.invitations_queued} invites</span>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      <BulkImportStaff
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => refresh()}
      />
    </Layout>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 h-8 rounded-full text-[11px] font-bold border transition-all ${
        active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
      }`}
    >
      {label}
    </button>
  );
}
